import React, { useState, useEffect } from 'react';
import { dashboardApi, shiftsApi, companyDuesApi } from '../../services/api';
import { fmt } from '../../utils/billing';
import { useAuth } from '../../context/AuthContext';
import RoomGridPage from './RoomGridPage';
import GuestTasksWidget from './GuestTasksWidget';

export default function Dashboard({ hotel, onNavigate, onCheckout, onEdit }) {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const profitFilter = 'monthly';
  const [customRange, setCustomRange] = useState({ start: '', end: '' });

  const [activeShifts, setActiveShifts] = useState([]);
  const [activeTab, setActiveTab] = useState('grid'); // Default to Grid for easy usage
  const [companyDues, setCompanyDues] = useState([]);

  useEffect(() => {
    if (!hotel) return;
    loadStats();
    companyDuesApi.list(hotel.id).then(setCompanyDues).catch(() => setCompanyDues([]));
    if (user?.role === 'superadmin') {
      loadActiveShifts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel, profitFilter, customRange, user]);

  async function loadActiveShifts() {
    try {
      const data = await shiftsApi.active(hotel.id);
      setActiveShifts(data);
    } catch (err) { console.error(err); }
  }

  async function loadStats() {
    setLoading(true);
    try {
      const data = await dashboardApi.stats(hotel.id, customRange.start, customRange.end);
      
      setStats({
        active: data.active,
        todayIn: data.todayIn,
        todayOut: data.todayOut,
        tomorrowIn: data.tomorrowIn,
        confirmed: [],
        cash: Number(data.todayPay.cash),
        upi: Number(data.todayPay.upi),
        card: Number(data.todayPay.card),
        cheque: Number(data.todayPay.cheque),
        allPays: Number(data.todayPay.total),
        shiftAnalytics: data.shiftAnalytics || [],
        todayExp: Number(data.expenses.today_exp),
        totalExp: Number(data.expenses.total_exp),
        totalRooms: Number(data.roomStats.total),
        occupied: Number(data.roomStats.occupied),
        vacant: Number(data.roomStats.vacant),
        dirty: Number(data.roomStats.dirty),
        occupancyRate: data.roomStats.total > 0 ? Math.round((data.roomStats.occupied / data.roomStats.total) * 100) : 0,
        expBreakdown: data.expBreakdown.reduce((acc, curr) => {
          acc[curr.category] = Number(curr.amount);
          return acc;
        }, {}),
        adr: Math.round(Number(data.adr) || 0),
        revpar: Math.round(Number(data.revpar) || 0),
        pendingBalances: data.pendingBalances || [],
        occupancyTrend: data.occupancyTrend || []
      });
    } catch (err) {
      console.error('Dashboard Stats Error:', err);
    } finally {
      setLoading(false);
    }
  }

  if (!hotel) return <div className="empty"><div className="empty-icon">🏨</div>Select a hotel to view dashboard</div>;

  const {
    active = [], todayIn = [], todayOut = [],
    cash = 0, upi = 0, card = 0, cheque = 0, allPays = 0,
    todayExp = 0, totalExp = 0,
    shiftAnalytics = [],
    pendingBalances = []
  } = stats || {};

  const overdueDues = companyDues.filter(d => d.is_overdue);
  const totalCompanyDue = companyDues.reduce((s, d) => s + Number(d.balance || 0), 0);

  return (
    <div className="dash-container">
      {companyDues.length > 0 && (
        <div onClick={() => onNavigate?.('shifts')} style={{ cursor: 'pointer', marginBottom: 16, padding: '12px 16px', borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          background: overdueDues.length ? 'rgba(239,68,68,0.08)' : 'rgba(232,184,109,0.12)', border: `1.5px solid ${overdueDues.length ? 'var(--danger)' : 'rgba(232,184,109,0.5)'}` }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>
            💼 {companyDues.length} company bill{companyDues.length > 1 ? 's' : ''} unpaid — {fmt(totalCompanyDue)} to collect
            {overdueDues.length > 0 && <span style={{ color: 'var(--danger)', marginLeft: 8 }}>⚠️ {overdueDues.length} overdue (24h+)</span>}
          </div>
          <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--accent)' }}>Open Cash Management →</span>
        </div>
      )}
      {/* 🟢 NAVIGATION TABS */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
        <button 
          onClick={() => setActiveTab('grid')}
          style={{ padding: '12px 20px', fontSize: 13, fontWeight: 700, border: 'none', background: 'transparent', color: activeTab === 'grid' ? 'var(--accent)' : 'var(--muted)', borderBottom: activeTab === 'grid' ? '3px solid var(--accent)' : '3px solid transparent', cursor: 'pointer' }}
        >🗺️ Live Room Grid</button>
        <button 
          onClick={() => setActiveTab('summary')}
          style={{ padding: '12px 20px', fontSize: 13, fontWeight: 700, border: 'none', background: 'transparent', color: activeTab === 'summary' ? 'var(--accent)' : 'var(--muted)', borderBottom: activeTab === 'summary' ? '3px solid var(--accent)' : '3px solid transparent', cursor: 'pointer' }}
        >📊 Business Summary</button>
      </div>

      {activeTab === 'grid' && (
        <div style={{ marginBottom: 24 }}>
          <RoomGridPage hotel={hotel} onCheckout={onCheckout} onEdit={onEdit} onNavigate={onNavigate} onSuccess={loadStats} />
          <GuestTasksWidget hotel={hotel} user={user} />
        </div>
      )}

      {activeTab === 'summary' && (
        <>
          <div style={{ display: 'flex', gap: 15, marginBottom: 20, alignItems: 'center', background: 'var(--s1)', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>Filter Transactions by Date:</span>
            <input type="date" className="input" style={{ width: 140, padding: '8px 12px' }} value={customRange.start} onChange={e => setCustomRange({...customRange, start: e.target.value})} />
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)' }}>to</span>
            <input type="date" className="input" style={{ width: 140, padding: '8px 12px' }} value={customRange.end} onChange={e => setCustomRange({...customRange, end: e.target.value})} />
            {(customRange.start || customRange.end) && (
               <button className="btn btn-sm btn-ghost" onClick={() => setCustomRange({ start: '', end: '' })}>Clear Filter</button>
            )}
          </div>
          {loading && !stats ? (
            <div className="empty">Loading Summary Stats…</div>
          ) : (
            <>
              {/* TOP ROW: REVENUE OVERVIEW (Moved up as primary summary) */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                {[
                  { lbl: 'Total Collections', val: fmt(allPays), sub: 'Today (All Modes)', icon: '💰', color: 'var(--accent)' },
                  { lbl: "Today's Cash", val: fmt(cash), sub: 'Cash receipts today only', icon: '💵', color: '#6dbf94' },
                  { lbl: 'UPI / Online', val: fmt(upi), sub: 'Digital transfers today', icon: '📱', color: 'var(--teal)' },
                  { lbl: 'Pending Stay', val: active.length, sub: 'In-house Guest Groups', icon: '🏨', color: 'var(--gold)' },
                ].map((s, i) => (
                    <div key={i} className="panel" style={{ padding: '16px 20px', borderLeft: `4px solid ${s.color}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 4 }}>{s.lbl}</div>
                          <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.val}</div>
                        </div>
                        <div style={{ fontSize: 22 }}>{s.icon}</div>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted2)', marginTop: 4 }}>{s.sub}</div>
                    </div>
                ))}
              </div>

              {/* 🔴 ROW: FINANCIALS & EXPENSES & PENDING BALANCES */}
              {['superadmin', 'manager', 'staff'].includes(user?.role) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr', gap: 16, marginBottom: 20 }}>
                  <div className="panel">
                    <div className="panel-head">
                      <span className="panel-title">{customRange.start || customRange.end ? 'Selected Dates' : "Today's"} Cash Flow</span>
                      <span className="panel-sub">Split by mode</span>
                    </div>
                    <div style={{ padding: '16px 18px' }}>
                      {[
                        { label: '💵 Cash Receipts', amount: cash, color: '#6dbf94' },
                        { label: '📱 UPI Transfers', amount: upi, color: '#7ec8c8' },
                        { label: '💳 Card Swipe', amount: card, color: '#e8b86d' },
                        { label: '📝 Cheque', amount: cheque, color: '#9b59b6' },
                      ].map((p, i) => {
                        const total = cash + upi + card + cheque || 1;
                        return (
                          <div key={i} className="rev-bar-row">
                            <div className="rev-bar-label">{p.label}</div>
                            <div className="rev-bar-bg">
                              <div className="rev-bar-fill" style={{ width: `${(p.amount / total) * 100}%`, background: p.color }}></div>
                            </div>
                            <div className="rev-bar-amt" style={{ color: p.color }}>{fmt(p.amount)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="panel">
                    <div className="panel-head">
                      <span className="panel-title">Expenses Tracking</span>
                      <span className="panel-sub">Daily maintenance &amp; overheads</span>
                    </div>
                    <div style={{ padding: '20px 24px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: 10, marginBottom: 10 }}>
                        <span style={{ fontSize: 13, color: 'var(--muted)' }}>{customRange.start || customRange.end ? 'Selected Dates' : "Today's"} Expenses</span>
                        <span style={{ fontWeight: 700, color: 'var(--danger)' }}>{fmt(todayExp)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 13, color: 'var(--muted)' }}>Total Cumulative Expenses</span>
                        <span style={{ fontWeight: 700 }}>{fmt(totalExp)}</span>
                      </div>
                    </div>
                  </div>

                  {/* PENDING BALANCES WIDGET */}
                  <div className="panel">
                    <div className="panel-head">
                      <span className="panel-title">Pending Guest Balances</span>
                      <span className="panel-sub">Checked-in reservations</span>
                    </div>
                    <div className="tbl-wrap" style={{ maxHeight: 200, overflowY: 'auto' }}>
                      <table className="tbl-sm">
                        <tbody>
                          {pendingBalances.length === 0 ? (
                            <tr><td colSpan={3}><div className="empty" style={{ padding: '20px 0', fontSize: 11 }}>All checked-in guests have paid 0 balance</div></td></tr>
                          ) : pendingBalances.map(r => (
                            <tr key={r.id}>
                              <td style={{ padding: '8px 12px' }}>
                                <div className="td-main" style={{ fontSize: 12 }}>{r.guest_name || '—'}</div>
                                <div className="td-sub" style={{ fontSize: 10, color: 'var(--danger)', fontWeight: 700 }}>Due: {fmt(Number(r.balance))}</div>
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                <div style={{ fontSize: 12 }}>Room {r.room || 'N/A'}</div>
                              </td>
                              <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                                <button className="btn btn-sm btn-ghost" style={{ fontSize: 13, padding: 4 }} onClick={() => onCheckout(r.id)} title="Collect Payment">💵</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* TODAY IN & OUT WIDGETS */}
              {['superadmin', 'manager', 'staff'].includes(user?.role) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                  <div className="panel">
                    <div className="panel-head">
                      <span className="panel-title">Today's Check-ins</span>
                      <span className="panel-sub">{todayIn.length} Arrivals</span>
                    </div>
                    <div className="tbl-wrap" style={{ maxHeight: 250, overflowY: 'auto' }}>
                      <table className="tbl-sm">
                        <tbody>
                          {todayIn.length === 0 ? (
                            <tr><td colSpan={3}><div className="empty" style={{ padding: '20px 0', fontSize: 11 }}>No check-ins today</div></td></tr>
                          ) : todayIn.map(r => {
                            const isPreBooking = r.status === 'confirmed' || r.status === 'reserved';
                            const isCheckedIn = r.status === 'checked-in';
                            return (
                              <tr key={r.id} style={{ background: isPreBooking ? 'rgba(232,184,109,0.06)' : 'transparent' }}>
                                <td style={{ padding: '8px 12px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {isPreBooking && (
                                      <span title="Pre-booked — awaiting check-in" style={{ fontSize: 10, background: 'rgba(232,184,109,0.2)', color: 'var(--gold)', padding: '1px 6px', borderRadius: 8, fontWeight: 800, border: '1px solid rgba(232,184,109,0.4)', whiteSpace: 'nowrap' }}>
                                        📋 PRE-BOOKED
                                      </span>
                                    )}
                                    {isCheckedIn && (
                                      <span title="Already checked in" style={{ fontSize: 10, background: 'rgba(109,191,148,0.15)', color: 'var(--success)', padding: '1px 6px', borderRadius: 8, fontWeight: 800, border: '1px solid rgba(109,191,148,0.3)', whiteSpace: 'nowrap' }}>
                                        ✅ IN
                                      </span>
                                    )}
                                  </div>
                                  <div className="td-main" style={{ fontSize: 12, marginTop: 2 }}>{r.guest_name || r.name || '—'}</div>
                                  <div className="td-sub" style={{ fontSize: 10, color: 'var(--muted)' }}>{r.phone || 'No phone'}</div>
                                </td>
                                <td style={{ padding: '8px 12px' }}>
                                  <div style={{ fontSize: 12, fontWeight: 700 }}>{r.room ? `Room ${r.room}` : 'Unassigned'}</div>
                                  <div style={{ fontSize: 10, color: isCheckedIn ? 'var(--success)' : 'var(--gold)' }}>
                                    {isPreBooking ? 'Awaiting Check-in' : r.status.toUpperCase()}
                                  </div>
                                </td>
                                <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                                  {isPreBooking ? (
                                    <button
                                      className="btn btn-sm btn-gold"
                                      style={{ fontSize: 11, padding: '4px 10px' }}
                                      onClick={() => onEdit?.(r.id)}
                                      title="Complete Check-in"
                                    >
                                      ✔ Check In
                                    </button>
                                  ) : (
                                    <button className="btn btn-sm btn-ghost" style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--border)' }} onClick={() => onEdit?.(r.id)} title="Edit Check-in">✏️ Edit</button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {(() => {
                    const uniqueOut = Array.isArray(todayOut) ? todayOut.filter((item, index, self) =>
                      index === self.findIndex((t) => t.id === item.id)
                    ) : [];
                    return (
                      <div className="panel">
                        <div className="panel-head">
                          <span className="panel-title">Today's Check-outs</span>
                          <span className="panel-sub">{uniqueOut.length} Departures</span>
                        </div>
                        <div className="tbl-wrap" style={{ maxHeight: 250, overflowY: 'auto' }}>
                          <table className="tbl-sm">
                            <tbody>
                              {uniqueOut.length === 0 ? (
                                <tr><td colSpan={3}><div className="empty" style={{ padding: '20px 0', fontSize: 11 }}>No check-outs today</div></td></tr>
                              ) : uniqueOut.map(r => {
                                const isCheckedOut = r.status === 'checked-out';
                                return (
                                  <tr key={r.id}>
                                    <td style={{ padding: '8px 12px' }}>
                                      <div className="td-main" style={{ fontSize: 12 }}>{r.guest_name || r.guestName || r.name || '—'}</div>
                                      <div className="td-sub" style={{ fontSize: 10, color: 'var(--muted)' }}>{r.phone || r.guestPhone || r.guest_phone || 'No phone'}</div>
                                    </td>
                                    <td style={{ padding: '8px 12px' }}>
                                      <div style={{ fontSize: 12, fontWeight: 700 }}>{r.room ? `Room ${r.room}` : 'Unassigned'}</div>
                                      <div style={{ fontSize: 10, color: isCheckedOut ? 'var(--muted)' : 'var(--danger)' }}>
                                        {isCheckedOut ? 'CHECKED OUT' : (r.status || '').toUpperCase()}
                                      </div>
                                    </td>
                                    <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                                      {isCheckedOut ? (
                                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', padding: '4px 8px', background: 'var(--s1)', borderRadius: 4, border: '1px solid var(--border)', display: 'inline-block' }}>
                                          Checked Out
                                        </span>
                                      ) : (
                                        <button className="btn btn-sm btn-ghost" style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--border)' }} onClick={() => onEdit?.(r.id)} title="Edit Check-out">✏️ Edit</button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

                {user?.role === 'superadmin' && (
                  <>
                    <div style={{ marginBottom: 20 }}>
                      <div className="panel">
                        <div className="panel-head">
                          <span className="panel-title">Shift-wise Collections & Performance</span>
                          <span className="panel-sub">{customRange.start || customRange.end ? 'Filtered shifts summary' : 'Recent 5 shifts summary'}</span>
                        </div>
                        <div className="tbl-wrap">
                          <table className="tbl">
                            <thead>
                              <tr>
                                <th>Staff / Shift</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'center' }}>Bookings</th>
                                <th style={{ textAlign: 'center' }}>Checkouts</th>
                                <th style={{ textAlign: 'right' }}>Cash</th>
                                <th style={{ textAlign: 'right' }}>UPI</th>
                                <th style={{ textAlign: 'right' }}>Card</th>
                                <th style={{ textAlign: 'right' }}>Cheque</th>
                                <th style={{ textAlign: 'right' }}>Total Collection</th>
                              </tr>
                            </thead>
                            <tbody>
                              {shiftAnalytics.map(s => (
                                <tr key={s.id}>
                                  <td>
                                    <div style={{ fontWeight: 700 }}>{s.user_name}</div>
                                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>{s.user_shift} | {new Date(s.start_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</div>
                                  </td>
                                  <td>
                                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: s.status === 'active' ? 'var(--success-bg)' : 'var(--s1)', color: s.status === 'active' ? 'var(--success)' : 'var(--muted)' }}>
                                      {s.status.toUpperCase()}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'center', fontWeight: 700 }}>{s.stats.booked}</td>
                                  <td style={{ textAlign: 'center', fontWeight: 700 }}>{s.stats.checkouts}</td>
                                  <td style={{ textAlign: 'right' }}>{fmt(s.collection.cash)}</td>
                                  <td style={{ textAlign: 'right' }}>{fmt(s.collection.upi)}</td>
                                  <td style={{ textAlign: 'right' }}>{fmt(s.collection.card)}</td>
                                  <td style={{ textAlign: 'right' }}>{fmt(s.collection.cheque)}</td>
                                  <td style={{ textAlign: 'right', fontWeight: 800, color: 'var(--accent)' }}>{fmt(s.collection.total)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="grid-2-1" style={{ marginBottom: 20 }}>
                      <div className="panel">
                        <div className="panel-head">
                          <span className="panel-title">Operations Flow Summary</span>
                          <span className="panel-sub">Daily check-in / out volume</span>
                        </div>
                        <div style={{ padding: '20px 24px' }}>
                          <div style={{ display: 'flex', gap: 15, marginBottom: 20 }}>
                            <div style={{ flex: 1, padding: 12, borderRadius: 8, background: 'rgba(126,200,200,0.1)', border: '1px solid rgba(126,200,200,0.2)' }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--teal)' }}>TODAY IN</div>
                              <div style={{ fontSize: 18, fontWeight: 800 }}>{todayIn.length}</div>
                            </div>
                            <div style={{ flex: 1, padding: 12, borderRadius: 8, background: 'rgba(232,125,125,0.1)', border: '1px solid rgba(232,125,125,0.2)' }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--danger)' }}>TODAY OUT</div>
                              <div style={{ fontSize: 18, fontWeight: 800 }}>{todayOut.length}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '0 4px', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                            <span>Current In-House:</span>
                            <span style={{ fontWeight: 700 }}>{active.length} Groups</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* 🔵 ACTIVE SHIFTS (Admin Only) */}
        {user?.role === 'superadmin' && (
          <div className="panel" style={{ marginTop: 24 }}>
            <div className="panel-head">
              <span className="panel-title">On-Duty Staff (Active Shifts)</span>
              <span className="panel-sub">{activeShifts.length} Staff members online</span>
            </div>
            <div style={{ padding: '16px 20px' }}>
              {activeShifts.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>No active shifts at the moment.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                  {activeShifts.map(s => (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--s1)', padding: 12, borderRadius: 10, border: '1px solid var(--border)' }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--accent)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>{s.user_name?.[0].toUpperCase()}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{s.user_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>Started: {new Date(s.start_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {s.user_shift} Shift</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--success)' }}>Op. Cash</div>
                        <div style={{ fontSize: 13, fontWeight: 800 }}>{fmt(s.opening_cash)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
  );
}
